import React, { ReactNode, useEffect, useRef, useState } from 'react';
import AudioRecorder from '../../../audio/AudioRecorder';
import { Typography, Card, Button } from '@mui/material';
import './SubTaskView.css';
import { ProfileName, SubTask } from './models';
import AudioGainSlider from '../../../audio/AudioGainSlider';
import AudioLevelDisplay from '../../../audio/AudioLevelDisplay';
import { useLocalStorage } from '../../../../services/LocalStorageProvider';
import { translateReactNode } from '../../../../utils/translateReactNode';

const DEFAULT_VOICE_GAIN = 0.55;
const DEFAULT_BREATHE_GAIN = 0.9;
const DEFAULT_COUGH_GAIN = 0.1;
const MIC_PERMISSION_DENIED_ERROR = 'Microphone access was denied. Please allow microphone permission and reload the page.';
const MIC_ACCESS_ERROR = 'Could not access the microphone. Please check your device and browser settings.';

export interface GainProfile {
  name: ProfileName,
  value: number
}

const initializeGain = (profileName: ProfileName, gainProfiles: [GainProfile]) => {

  if (gainProfiles && gainProfiles.length > 0) {
    const profile = gainProfiles.find(x => x.name === profileName);
    if (profile) {
      return profile.value;
    }
  }

  switch (profileName) {
    case 'speech':
      return DEFAULT_VOICE_GAIN;
    case 'breathe':
      return DEFAULT_BREATHE_GAIN;
    case 'cough':
      return DEFAULT_COUGH_GAIN;
    default:
      return 1;
  }
};

interface SubTaskViewProps {
  children: ReactNode;
  onAudioRecorded: (audioSource: string) => void;
  onIsRecordingChange?: (isRecording: boolean) => void;
  onStreamChange?: (stream: MediaStream) => void;
  showAudioPreview?: boolean;
  showAudioBar?: boolean;
  subTask: SubTask;
  buttonLabel?: string;
  tutorialOpen?: boolean;
}



const SubTaskView: React.FC<SubTaskViewProps> = ({
  subTask,
  children,
  onAudioRecorded,
  onIsRecordingChange,
  onStreamChange,
  showAudioPreview = true,
  showAudioBar = false,
  buttonLabel = "Next",
  tutorialOpen = false,
}) => {
  const audioPreviewRef = useRef<HTMLAudioElement>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const originalStreamRef = useRef<MediaStream | null>(null);
  const isReleasingRef = useRef<boolean>(false);
  const { storage: localStorage, updateStorage: updateLocalStorage } = useLocalStorage();

  const [audioSource, setAudioSource] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [gain, setGain] = useState(() => initializeGain(subTask.profileName, localStorage.gainProfiles));
  const [device, setDevice] = useState<MediaDeviceInfo | null>(null);
  const [disableRecording, setDisableRecording] = useState<boolean>(() => localStorage.disableRecording || false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const [micError, setMicError] = useState<string | null>(null);

  const { stopwatchSeconds: timer, title, instructions, content, recordButtonDisabled, showGainSlider, buttonDisabled } = subTask;

  const releaseMicrophone = () => {
    if (originalStreamRef.current) {
      isReleasingRef.current = true;
      originalStreamRef.current.getAudioTracks().forEach(track => track.stop());
      originalStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(err => console.error('AudioContext close failed', err));
      audioContextRef.current = null;
    }
    gainNodeRef.current = null;
    setStream(null);

    // Once the mic is released, return the iOS audio session to playback so the
    // post-recording audio preview routes through the loudspeaker.
    const audioSession = (navigator as any).audioSession;
    if (audioSession) {
      audioSession.type = 'playback';
    }
  };

  const acquireMicrophone = async (): Promise<MediaStream | null> => {
    // Switch the audio session BEFORE getUserMedia. iOS Safari decides routing
    // at the moment the mic stream is created.
    const audioSession = (navigator as any).audioSession;
    if (audioSession) {
      audioSession.type = 'play-and-record';
    }

    const audioConstraints: MediaTrackConstraints = {
      autoGainControl: false,
      channelCount: 1,
      noiseSuppression: false,
      echoCancellation: false,
      sampleRate: 44100,
      sampleSize: 16
    };

    let rawStream: MediaStream | undefined;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const defaultDevice = devices.find(x => x.kind === 'audioinput' && x.label.startsWith('Default - '));
      if (defaultDevice) {
        setDevice(defaultDevice);
        rawStream = await navigator.mediaDevices.getUserMedia({ audio: { ...audioConstraints, deviceId: defaultDevice.deviceId } });
      } else {
        rawStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setMicError(MIC_PERMISSION_DENIED_ERROR);
        if (audioSession) audioSession.type = 'playback';
        return null;
      }
      console.warn('getUserMedia with strict constraints failed, falling back to default audio', err);
      try {
        rawStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (fallbackErr) {
        console.error('getUserMedia fallback also failed', fallbackErr);
        const message = fallbackErr instanceof DOMException && fallbackErr.name === 'NotAllowedError'
          ? MIC_PERMISSION_DENIED_ERROR
          : MIC_ACCESS_ERROR;
        setMicError(message);
        if (audioSession) audioSession.type = 'playback';
        return null;
      }
    }

    if (!rawStream) {
      if (audioSession) audioSession.type = 'playback';
      return null;
    }

    originalStreamRef.current = rawStream;

    const audioContext = audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(rawStream);

    const gainNode = gainNodeRef.current = audioContext.createGain();
    gainNode.gain.value = gain;
    source.connect(gainNode);

    const destination = audioContext.createMediaStreamDestination();
    gainNode.connect(destination);

    rawStream.getAudioTracks().forEach(track => {
      track.addEventListener('ended', () => {
        if (isReleasingRef.current) return;
        console.error('Microphone track ended unexpectedly (device disconnected)');
        setStream(null);
        setDisableRecording(true);
        updateLocalStorage({ isRecording: false, disableRecording: true });
        setMicError('Microphone was disconnected. Please reconnect your microphone and reload the page.');
      });
    });

    setStream(destination.stream);
    onStreamChange?.(destination.stream);
    return destination.stream;
  };

  useEffect(() => {
    // Hint iOS that the page is currently for playback only — demos, instructions,
    // and the audio preview should all route to the loudspeaker. We flip to
    // 'play-and-record' inside acquireMicrophone() and back here on release.
    const audioSession = (navigator as any).audioSession;
    if (audioSession) {
      audioSession.type = 'playback';
    }

    if (localStorage.isRecording) {
      updateLocalStorage({ isRecording: false });
    }
    if (localStorage.disableRecording) {
      updateLocalStorage({ disableRecording: false });
    }

    if (!subTask.createdAt) {
      subTask.createdAt = new Date(Date.now()).toISOString();
    }

    return () => {
      releaseMicrophone();
      updateLocalStorage({ isRecording: false, disableRecording: false });
      stopPlayingAudioPreview();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = gain;
    }

    updateGainProfile(subTask.profileName, gain);
  }, [gain]);

  useEffect(() => {
    stream?.getAudioTracks().forEach(track => track.stop());
  }, [showGainSlider]);

  useEffect(() => {
    if (isRecording) {
      stopPlayingAudioPreview();
    }

    updateLocalStorage({ isRecording });
    onIsRecordingChange?.(isRecording);
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording && audioSource && nextButtonRef.current) {
      nextButtonRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isRecording, audioSource]);

  useEffect(() => {
    if (localStorage.disableRecording !== disableRecording) {
      setDisableRecording(localStorage.disableRecording);
    }
  }, [localStorage.disableRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkButtonDisabled = async () => {
      let isDisabled = false;
      if (buttonDisabled) {
        isDisabled = await buttonDisabled(subTask, audioSource || undefined);
      }
      if (isDisabled !== nextButtonDisabled) {
        setNextButtonDisabled(isDisabled);
      }
    }
    checkButtonDisabled();
  }, [audioSource, buttonDisabled, subTask, nextButtonDisabled]);

  const stopPlayingAudioPreview = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current.currentTime = 0;
    }
  };

  const updateGainProfile = (name: ProfileName, gain: number) => {
    const gainProfiles: [GainProfile] = localStorage.gainProfiles || [];
    const profile = gainProfiles.find(x => x.name === name);
    if (profile) {
      if (gain !== profile.value) {
        console.log('Updating gain profile', name, gain);
        profile.value = gain;
        updateLocalStorage({ ...gainProfiles });
      }
    } else {
      console.log('Adding new gain profile', name, gain);
      gainProfiles.push({ name, value: gain });
      updateLocalStorage({ gainProfiles });
    }
  }

  const handleOnAudioRecorded = async (blob: Blob) => {
    setAudioSource(URL.createObjectURL(blob));
    subTask.inputGain = gain;
    subTask.microphone = `Web Audio: ${device?.label || 'Unknown Device'}`;
  };

  return (
    <div className='subTask'>
      <div className={tutorialOpen ? 'disabled' : ''}>
      {title && <Typography variant='h4' component='h2' className='subtask-title' >{title}</Typography>}
      {instructions && <Typography variant='h5' component='div' className='subtask-instructions'>{instructions}</Typography>}

      {content && (
        <div style={{ alignContent: 'center', textAlign: 'center' }} className='subtask-content'>
          {content}
        </div>
      )}

      <div>{children}</div>

      <div style={{ marginTop: '1rem', textAlign: 'center' }}>

        {micError && (
          <Typography variant="body1" color="error" style={{ marginBottom: '1rem' }}>
            {translateReactNode(micError)}
          </Typography>
        )}

        {!micError && (
          <>
            <div className={tutorialOpen || recordButtonDisabled || disableRecording ? 'disabled' : ''}>
              <AudioRecorder
                stream={stream}
                onAcquireStream={acquireMicrophone}
                onReleaseStream={releaseMicrophone}
                onAudioRecorded={handleOnAudioRecorded}
                handleIsRecording={setIsRecording}
                timer={timer}
                shouldStop={tutorialOpen}
                disabled={tutorialOpen || recordButtonDisabled || disableRecording} />
            </div>

            {showGainSlider && <AudioGainSlider gain={gain} setGain={setGain} />}
            {showAudioBar && stream && <AudioLevelDisplay stream={stream} />}
          </>
        )}
        {showAudioPreview && audioSource && (
          <Card className={"audio-preview " + (isRecording ? "disabled": "")} >
            <Typography variant="h6" component='div'>{translateReactNode("Audio Preview")}</Typography>
            <audio ref={audioPreviewRef} controls src={audioSource}></audio>
          </Card>
        )}
      </div>

      {audioSource && (
        <Button variant="contained"
          onClick={() => onAudioRecorded(audioSource)}
          disabled={isRecording || !audioSource || nextButtonDisabled}
          style={{
            marginTop: '1rem'
          }}
        >
          {translateReactNode(buttonLabel)}
        </Button>
      )}
      </div>
    </div>
  );
};

export default SubTaskView;
